'use client';

import { pushToDataLayer, DataLayerEvent } from 'components/helpers/Global/Analytics/DataLayer';

export type SearchAnalyticsType = 'Standard' | 'TypeAhead' | 'PreSearch' | string;

export interface SearchActionPayload {
  searchType: SearchAnalyticsType;
  siteSearchPhrase?: string;
}

export interface SearchFilterClearPayload {
  searchType: SearchAnalyticsType;
  siteSearchPhrase?: string;
}

export interface SearchFilterPayload {
  searchType: SearchAnalyticsType;
  siteSearchPhrase?: string;
  facetFilter: string[];
  facetFilterValue: string[];
}

export interface SearchResultPayload {
  searchType: SearchAnalyticsType;
  contentTitle: string;
  language?: string;
  facetFilter?: string[];
  facetFilterValue?: string[];
  recordType?: string;
  contentType?: string;
  searchSortType?: string;
  siteSearchPhrase?: string;
  labelBadge?: string;
  pagination?: string;
  filteredResult?: 'yes' | 'no';
  productName?: string;
  productId?: string;
  searchResultCount?: string;
}

export interface SearchSortPayload {
  searchType: SearchAnalyticsType;
  siteSearchPhrase?: string;
  searchSortType: string;
}

export interface SearchPageChangePayload {
  searchType: SearchAnalyticsType;
  siteSearchPhrase?: string;
  pagination: string;
}

const normalizeSearchTypeValue = (searchType?: string | null): SearchAnalyticsType | undefined => {
  if (!searchType) {
    return undefined;
  }

  const normalizedValue = searchType.trim().toLowerCase();

  if (normalizedValue === 'presearch') {
    return 'PreSearch';
  }

  if (normalizedValue === 'typeahead') {
    return 'TypeAhead';
  }

  if (normalizedValue === 'standard') {
    return 'Standard';
  }

  return searchType;
};

const resolveSearchAnalyticsType = (searchType?: SearchAnalyticsType): SearchAnalyticsType => {
  const normalizedInputType = normalizeSearchTypeValue(searchType) ?? 'Standard';

  if (typeof window === 'undefined') {
    return normalizedInputType;
  }

  const urlSearchType = new URLSearchParams(window.location.search).get('searchType');
  const normalizedUrlType = normalizeSearchTypeValue(urlSearchType);

  if (normalizedUrlType && normalizedInputType === 'Standard') {
    return normalizedUrlType;
  }

  return normalizedInputType;
};

const pushSearchAnalyticsEvent = (payload: DataLayerEvent): void => {
  console.log('[SearchAnalytics]', payload);
  pushToDataLayer(payload);
};

export const pushSearchIntentEvent = (): void => {
  const payload: DataLayerEvent = {
    event: 'search_intent',
  };

  pushSearchAnalyticsEvent(payload);
};

export const pushSearchActionEvent = ({
  searchType,
  siteSearchPhrase,
}: SearchActionPayload): void => {
  const payload: DataLayerEvent = {
    event: 'search_action',
    search_type: resolveSearchAnalyticsType(searchType),
    site_search_phrase: siteSearchPhrase,
  };

  pushSearchAnalyticsEvent(payload);
};

export const pushSearchFilterClearEvent = ({
  searchType,
  siteSearchPhrase,
}: SearchFilterClearPayload): void => {
  const payload: DataLayerEvent = {
    event: 'search_filter_clear',
    search_type: resolveSearchAnalyticsType(searchType),
    site_search_phrase: siteSearchPhrase,
  };

  pushSearchAnalyticsEvent(payload);
};

export const pushSearchFilterEvent = ({
  searchType,
  siteSearchPhrase,
  facetFilter,
  facetFilterValue,
}: SearchFilterPayload): void => {
  const payload: DataLayerEvent = {
    event: 'search_filter',
    search_type: resolveSearchAnalyticsType(searchType),
    site_search_phrase: siteSearchPhrase,
    facet_filter: facetFilter,
    facet_filter_value: facetFilterValue,
  };

  pushSearchAnalyticsEvent(payload);
};

export const pushSearchSortEvent = ({
  searchType,
  siteSearchPhrase,
  searchSortType,
}: SearchSortPayload): void => {
  const payload: DataLayerEvent = {
    event: 'search_sort',
    search_type: resolveSearchAnalyticsType(searchType),
    site_search_phrase: siteSearchPhrase,
    search_sort_type: searchSortType,
  };

  pushSearchAnalyticsEvent(payload);
};

export const pushSearchPageChangeEvent = ({
  searchType,
  siteSearchPhrase,
  pagination,
}: SearchPageChangePayload): void => {
  const payload: DataLayerEvent = {
    event: 'search_page_change',
    search_type: resolveSearchAnalyticsType(searchType),
    site_search_phrase: siteSearchPhrase,
    pagination,
  };

  pushSearchAnalyticsEvent(payload);
};

export const pushSearchResultEvent = ({
  searchType,
  contentTitle,
  language,
  facetFilter,
  facetFilterValue,
  recordType,
  contentType,
  searchSortType,
  siteSearchPhrase,
  labelBadge,
  pagination,
  filteredResult,
  productName,
  productId,
  searchResultCount,
}: SearchResultPayload): void => {
  const payload: DataLayerEvent = {
    event: 'search_result',
    search_type: resolveSearchAnalyticsType(searchType),
    content_title: contentTitle,
    language: language,
    facet_filter: facetFilter,
    facet_filter_value: facetFilterValue,
    record_type: recordType,
    content_type: contentType,
    search_sort_type: searchSortType,
    site_search_phrase: siteSearchPhrase,
    'label/badge': labelBadge,
    pagination,
    filtered_result: filteredResult,
    product_name: productName,
    product_id: productId,
    search_result_count: searchResultCount,
  };

  pushSearchAnalyticsEvent(payload);
};
